<script setup lang="ts">
import { describeCron, toolEntryNames } from '@zerohuman/shared'
import {
    Button,
    Checkbox,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
    ZhEmptyState,
    ZhGeneratedAvatar,
    ZhLayerIcon,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhStepBar,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
    type AgentConnection,
    api,
    asListResponse,
    type CatalogClonedKpi,
    type CatalogCloneReview,
    type CatalogHeldRole,
    type CatalogReviewSlot,
    type CatalogReviewTool,
    type CatalogRole,
    type CatalogScheduleAllowance,
    type CatalogSlotBinding,
    type CatalogSlotFill,
    type CatalogTask,
    type CatalogTeam,
    type CatalogTeamCloneInput,
    type CatalogTeamCloneReview,
    type CatalogTeamPlanSize,
    type CatalogToolSwap,
    CRAFTED_NOT_IN_PLAN_MESSAGE,
    isCraftedRefusal,
    isTeamSizeRefusal,
    type Member,
    refusalUnit,
    requestFailure,
    type Team,
    type TeamKpiSignOffEdit,
    teamSizeSentence,
} from '@/api'
import ListSearchField from '@/components/ListSearchField.vue'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { expectedCostPerRun, roleLine, teamLine } from '@/lib/catalogText'
import { cronTime, withCronTime } from '@/lib/cronTime'
import { buildGateBadges } from '@/lib/gateBadges'
import { kpiBandText, kpiCadenceText, kpiTargetText } from '@/lib/kpi'
import { memberLabel } from '@/lib/memberLabel'
import { monthlyCostLines } from '@/lib/monthlyCost'
import { cloneOnLabel, entryOwnerVersion } from '@/lib/ownerVersion'
import { toolLabel } from '@/lib/presentation'
import { connectToolLocation } from '@/lib/teamSetup'

/** Why an off template still offers Clone (#1811): a clone has its own switch and never inherits the publisher's. */
const OFF_HINT = 'The publisher has switched this team off. You can still clone it, and your clone starts on.'

/**
 * `filterQuery` is the Catalog's shared business-function and needs-no-tools filters (#1727), owned by CatalogView as
 * the search is, so they carry over between Teams, Roles and Tasks. The server does the filtering.
 */
const props = defineProps<{
    kind: 'team' | 'role'
    teams: Team[]
    searchQuery?: string
    filterQuery?: Record<string, string>
}>()
const emit = defineEmits<{ adopted: []; teamCreated: [team: Team] }>()
const entries = ref<Array<CatalogRole | CatalogTeam>>([])
const selected = ref<CatalogRole | CatalogTeam | null>(null)
const page = ref(1)
const pages = ref(1)
const search = ref('')
const effectiveSearch = computed(() => props.searchQuery ?? search.value)
/** Whether the list is narrowed at all, so an empty one says nothing matched rather than nothing is published. */
const narrowed = computed(() => Boolean(effectiveSearch.value) || Object.keys(props.filterQuery ?? {}).length > 0)
/** One line under a card's name: a team's description (or charter purpose), or what a role's tasks are. */
function cardLine(entry: CatalogRole | CatalogTeam): string {
    return 'teammates' in entry ? teamLine(entry) : roleLine(entry)
}
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const status = ref('')
/** Which plan refusal the last clone met, if any: each has its own sentence and its own link to Billing. */
const upgrade = ref<'members' | 'size' | 'schedules' | 'crafted' | null>(null)
/**
 * The open team's size when the plan does not include it (os#1624), from the list row or the review: the form says
 * so, links to Billing and offers no clone. Null when it fits, or when nothing says (a community team, an older API).
 */
const sizeNotInPlan = ref<CatalogTeamPlanSize | null>(null)
/** A size report that says the plan does not include the team, or null. */
function shortfall(size: CatalogTeamPlanSize | null | undefined): CatalogTeamPlanSize | null {
    return size && !size.fits ? size : null
}
/** The sentence under a team card whose size the plan does not include, or '' for any other card. */
function cardSizeLine(entry: CatalogRole | CatalogTeam): string {
    const short = 'teammates' in entry ? shortfall(entry.planSize) : null
    return short ? teamSizeSentence(short) : ''
}
const teamId = ref('')
/** The team Select's value for "Create a team…": the kit Select refuses an empty value, and team ids are UUIDs. */
const NEW_TEAM = ':new-team'
/** The teams created from the team Select while the panel is up, offered beside the company's until it reads again. */
const createdTeams = ref<Team[]>([])
const teamChoices = computed(() => [
    ...props.teams,
    ...createdTeams.value.filter(created => !props.teams.some(team => team.id === created.id)),
])
/** The team a role clone lands on: the one chosen, and none while "Create a team…" is. */
const destinationTeamId = computed(() => (teamId.value === NEW_TEAM ? '' : teamId.value))
const newTeamName = ref('')
const creatingTeam = ref(false)
const newTeamError = ref('')
/**
 * Creates the team named under "Create a team…", with the name alone: its lead, goal and limits are set on its page.
 * Created, it is chosen as the destination and the Catalog is told, so its other team lists read again. A refused
 * create keeps the name typed, with the reason.
 */
async function createTeam() {
    const teamName = newTeamName.value.trim()
    if (!teamName || creatingTeam.value) return
    creatingTeam.value = true
    newTeamError.value = ''
    try {
        const created = await api.createTeam({ name: teamName })
        createdTeams.value = [...createdTeams.value, created]
        teamId.value = created.id
        newTeamName.value = ''
        emit('teamCreated', created)
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        newTeamError.value =
            code === 'team_slug_taken'
                ? 'Your company already has a team with this name. Choose another.'
                : 'Could not create the team. Try again.'
    } finally {
        creatingTeam.value = false
    }
}
/**
 * Teams arriving after a role was opened for review with none (the Catalog reads them as the panel shows): the review
 * started on "Create a team…" for want of one, so it moves to the first team unless a name is already being typed.
 */
watch(
    () => props.teams,
    teams => {
        if (teamId.value === NEW_TEAM && !newTeamName.value && teams[0]) teamId.value = teams[0].id
    },
)
const name = ref('')
const slug = ref('')
/**
 * One name per teammate (#1697 O15). Each starts blank: a blank teammate is cloned unnamed and shown by its role title,
 * which is its placeholder. "Pick names for me" fills blanks only, and `pickedNames` keeps what it filled each with,
 * so a kept picked name can be told from a typed one.
 */
const teammateNames = ref<string[]>([])
const pickedNames = ref<string[]>([])
const picking = ref(false)
const pickError = ref('')
const selectedTaskIds = ref<string[]>([])
/**
 * How each task stands with the company, as the Tasks tab says it: whether the company already holds a linked clone
 * of it, on which of the owner's versions, and whether the owner has published a newer one. A role clone leaves a
 * held task out: the company already has it, and the role's holder runs it on the team. A team clone keeps it ticked,
 * and the clone reuses it as it is rather than copying it, since a task belongs to its role, not a team. A held task
 * that is behind can be synced here, as on the Tasks tab.
 */
const heldByMe = (task: CatalogTask) => task.clonedByMe === true
/** The owner's version a Sync would move the company's clone onto, while it is behind; null when current or unknown. */
function availableVersion(task: CatalogTask): number | null {
    return heldByMe(task) && task.cloneStale && !task.ownedByMe && task.status !== 'deprecated'
        ? entryOwnerVersion(task.currentVersion)
        : null
}
/** "on 3, 5 available", then "on 5" once current; nothing when the API sent no version number. */
function heldLine(task: CatalogTask): string | null {
    if (task.ownedByMe) return 'Your company owns this task.'
    return task.cloneOnVersion == null ? null : cloneOnLabel(task.cloneOnVersion, availableVersion(task))
}
/** Per task, the sync in flight on it or how it ended; a note says why one failed. */
const syncing = ref<Record<string, 'syncing' | 'synced' | 'failed'>>({})
const syncNotes = ref<Record<string, string>>({})
async function syncTask(task: CatalogTask) {
    if (syncing.value[task.id] === 'syncing' || busy.value) return
    syncing.value = { ...syncing.value, [task.id]: 'syncing' }
    syncNotes.value = { ...syncNotes.value, [task.id]: '' }
    try {
        await api.syncCatalogTask(task.id)
    } catch (caught) {
        syncing.value = { ...syncing.value, [task.id]: 'failed' }
        syncNotes.value = {
            ...syncNotes.value,
            [task.id]: isCraftedRefusal(caught)
                ? CRAFTED_NOT_IN_PLAN_MESSAGE
                : `Could not sync ${task.name || task.slug}. Try again.`,
        }
        return
    }
    // The company's clone is now on the owner's version the entry shows: the row says so without a re-read.
    const onVersion = entryOwnerVersion(task.currentVersion)
    const synced = (row: CatalogTask): CatalogTask =>
        row.id === task.id ? { ...row, cloneStale: false, cloneOnVersion: onVersion ?? row.cloneOnVersion } : row
    if (selected.value) selected.value = { ...selected.value, tasks: selected.value.tasks.map(synced) }
    entries.value = entries.value.map(entry => ({ ...entry, tasks: entry.tasks.map(synced) }))
    syncing.value = { ...syncing.value, [task.id]: 'synced' }
}
/** Whether a role clone has nothing left to install: every task on it is already the company's. */
const allHeld = computed(() => !isTeam.value && selectedTasks.value.length > 0 && selectedTasks.value.every(heldByMe))
/** The daily cap field's starting value while the enterprise's Day $ is unknown, and its bounds (USD). */
const FALLBACK_DAILY_CAP_USD = 10
const MIN_DAILY_CAP_USD = 0.01
const MAX_DAILY_CAP_USD = 10000
const money = ref<number | string>(FALLBACK_DAILY_CAP_USD)
/** Set once the user edits the daily cap: the enterprise's Day $ arriving later never overwrites what they typed. */
const moneyTyped = ref(false)
function typeMoney(value: string | number) {
    moneyTyped.value = true
    money.value = value
}
/**
 * The review read (C9): per task, the tools it needs that the company hasn't connected and the connected ones that
 * could stand in. Empty until it answers, and when it cannot be read: the clone then works as it always has.
 */
const toolReview = ref<CatalogCloneReview['tasks']>([])
/**
 * The team review's report of the template's roles the company already has (#1842). Null until it answers, when it
 * cannot be read, and from an API that does not send it: the clone screen then says nothing of what exists.
 */
const heldRoles = ref<CatalogHeldRole[] | null>(null)
/** The same review's tools grouped by type, with the reader's status for each (#1843). */
const reviewTools = ref<CatalogReviewTool[]>([])
/** The tool slots the template's tasks declare, each with the connection filling it and the ones that could. */
const reviewSlots = ref<CatalogReviewSlot[]>([])
/** The connection chosen per slot key. A slot with no entry keeps the one already filling it, or stays unfilled. */
const slotChoices = ref<Record<string, string>>({})
/** The Select's value for leaving a slot unfilled: the kit Select refuses an empty value, and binding ids are UUIDs. */
const NO_FILL = ':no-fill'
/** Whether the review read is out, has failed, or has answered: a failed one never reads as "all connected". */
const reviewState = ref<'loading' | 'failed' | 'loaded'>('loading')
/** The destination team the role review was last opened for, so a team change opens it again and nothing else does. */
let reviewedTeamId: string | undefined
/** What the user chose per task and missing tool, keyed by `swapKey`. A tool with no entry is kept (the default). */
const swapChoices = ref<Record<string, string>>({})
const KEEP = 'keep'
const DROP = 'drop'
const USE = 'use:'
const selectedTasks = computed(() =>
    (selected.value?.tasks ?? []).filter(task => task.origin !== 'os' && task.status !== 'deprecated'),
)
const isTeam = computed(() => props.kind === 'team')

/**
 * The team review's report of the caller's schedule allowance (#1840): what is already on and what the plan allows,
 * `limit` null when it has none. Null until the review answers, and when it cannot be read: the rows then start on
 * and no count is shown, and the server still refuses a clone past the plan.
 */
const scheduleAllowance = ref<CatalogScheduleAllowance | null>(null)
/** Per suggested schedule, by task id: whether the cloner has it switched on, and the cron it is sent with. */
const scheduleChoices = ref<Record<string, { enabled: boolean; cron: string }>>({})
/** The template's suggested schedules for the tasks a clone could take, in the template's order. */
const suggestedSchedules = computed(() => {
    const entry = selected.value
    if (!entry || !('teammates' in entry)) return []
    return (entry.schedules ?? []).filter(schedule => selectedTasks.value.some(task => task.id === schedule.taskId))
})
/**
 * One row per suggestion of a task the cloner kept, so unticking a task removes its row: its name, whether it is on,
 * its cron, and its time of day when it has one to change (`HH:MM`), else null and the time is shown read-only.
 */
const scheduleRows = computed(() =>
    suggestedSchedules.value
        .filter(schedule => selectedTaskIds.value.includes(schedule.taskId))
        .map(schedule => {
            const task = selectedTasks.value.find(candidate => candidate.id === schedule.taskId)
            const choice = scheduleChoices.value[schedule.taskId] ?? { enabled: false, cron: schedule.cron }
            return {
                taskId: schedule.taskId,
                name: task?.name || task?.slug || schedule.taskId,
                enabled: choice.enabled,
                cron: choice.cron,
                time: cronTime(choice.cron),
            }
        }),
)
/** The schedules the enterprise would have on after this clone: those it has, and the rows switched on. */
const scheduleCount = computed(
    () => (scheduleAllowance.value?.used ?? 0) + scheduleRows.value.filter(row => row.enabled).length,
)
/** The count against the plan, as the Schedules section heads it. Nothing while the allowance is unknown. */
const scheduleCounter = computed(() => {
    const allowance = scheduleAllowance.value
    if (!allowance) return ''
    return allowance.limit === null
        ? `${scheduleCount.value} schedules on your ${allowance.plan} plan, which is unlimited`
        : `${scheduleCount.value} of ${allowance.limit} schedules on your ${allowance.plan} plan`
})
/** Whether the plan has room for this row to be on, counting every other row that is. */
function scheduleRoom(taskId: string): boolean {
    const allowance = scheduleAllowance.value
    if (!allowance || allowance.limit === null) return true
    const others = scheduleRows.value.filter(row => row.enabled && row.taskId !== taskId).length
    return allowance.used + others < allowance.limit
}
/** A row that is off and that the plan has no room to switch on: it says why and links to Billing. */
function scheduleBlocked(row: { taskId: string; enabled: boolean }): boolean {
    return !row.enabled && !scheduleRoom(row.taskId)
}
/**
 * Every suggestion starts with its own cron, switched on in the template's order for as many as the plan has room
 * for; the rest start off. Run when a template is opened, and again when its review reports the allowance.
 */
function startSchedules() {
    const allowance = scheduleAllowance.value
    let room =
        !allowance || allowance.limit === null
            ? Number.POSITIVE_INFINITY
            : Math.max(0, allowance.limit - allowance.used)
    scheduleChoices.value = Object.fromEntries(
        suggestedSchedules.value.map(schedule => {
            const enabled = room > 0
            if (enabled) room -= 1
            return [schedule.taskId, { enabled, cron: schedule.cron }]
        }),
    )
}
/** Switches a row on or off. A row the plan has no room for stays off. */
function switchSchedule(taskId: string, value: boolean | 'indeterminate') {
    const choice = scheduleChoices.value[taskId]
    const enabled = value === true
    if (!choice || (enabled && !scheduleRoom(taskId))) return
    scheduleChoices.value = { ...scheduleChoices.value, [taskId]: { ...choice, enabled } }
}
/** A new time of day for a row: its cron's minute and hour change, and its days stay as suggested. */
function setScheduleTime(taskId: string, value: string | number) {
    const choice = scheduleChoices.value[taskId]
    const cron = choice ? withCronTime(choice.cron, String(value)) : null
    if (!choice || !cron) return
    scheduleChoices.value = { ...scheduleChoices.value, [taskId]: { ...choice, cron } }
}
/** What a team clone sends for its schedules: every row, on or off, in template order. None for no suggestions. */
function scheduleBody(): Pick<CatalogTeamCloneInput, 'schedules'> {
    if (suggestedSchedules.value.length === 0) return {}
    return {
        schedules: scheduleRows.value.map(row => ({ taskId: row.taskId, enabled: row.enabled, cron: row.cron })),
    }
}

/**
 * Where the cloned team's tasks save what they learn (#1841). Nothing is ticked: a cloned team's tasks may teach the
 * team layer and the enterprise layer of this enterprise, as its own tasks may, and either can be withdrawn later on
 * the team's page. The section only says which tasks write where.
 */
type MemoryLayer = 'team' | 'enterprise'
const MEMORY_LAYERS: readonly MemoryLayer[] = ['team', 'enterprise']
/**
 * One row per layer a kept task's current version may teach, from the team review, with the tasks that would write
 * there. Unticking the only task that writes a layer removes its row.
 */
const memoryRows = computed(() => {
    if (!isTeam.value) return []
    return MEMORY_LAYERS.flatMap(layer => {
        const writers = selectedTasks.value
            .filter(task => selectedTaskIds.value.includes(task.id))
            .filter(task => toolReview.value.find(row => row.taskId === task.id)?.memoryLayers?.includes(layer))
            .map(task => task.name || task.slug)
        return writers.length ? [{ layer, writers }] : []
    })
})
function memoryAllows(layer: MemoryLayer): string {
    const where = layer === 'team' ? `${name.value.trim() || 'this team'}'s memory` : "your enterprise's memory"
    return `This team's tasks add what they learn to ${where}, where other tasks can recall it.`
}

/** Each teammate's role titles, in the template's order. */
const teammateRoleTitles = computed(() => {
    const entry = selected.value
    if (!entry) return []
    return 'teammates' in entry
        ? entry.teammates.map(slot =>
              slot.roleIds.map(id => entry.roles.find(role => role.id === id)?.name ?? '').filter(Boolean),
          )
        : [[entry.name]]
})
const teammateRoles = computed(() => teammateRoleTitles.value.map(titles => titles.join(', ')))
/** What an unnamed teammate is shown as, the API's `memberDisplayName` rule: the first of its role titles. */
const teammatePlaceholders = computed(() => teammateRoleTitles.value.map(titles => [...titles].sort()[0] ?? ''))

/**
 * The enterprise's COO (#1697 O15), read each time a template is opened: the member holding the `coo` role. While it
 * has no name, the panel offers to name it and generate its avatar. Both are saved after the clone, through the
 * member rename and avatar upload, and only when the user touched them.
 */
const coo = ref<Member | null>(null)
const cooUnnamed = computed(() => Boolean(coo.value && !coo.value.name.trim()))
const cooTitle = computed(
    () => coo.value?.displayName?.trim() || coo.value?.roles.find(role => role.slug === 'coo')?.name || 'COO',
)
const cooName = ref('')
const cooPicked = ref('')
/** How many times "Generate another" was pressed: each press is a new avatar seed. */
const avatarRoll = ref(0)
const cooSeed = computed(() => {
    const base = cooName.value.trim() || cooTitle.value
    return avatarRoll.value ? `${base}-${avatarRoll.value}` : base
})
const cooAvatar = ref<{ toPngDataUrl: () => Promise<string> } | null>(null)
/** After a clone: what became of the COO, so the user knows where to finish naming it. */
const cooNotice = ref<'' | 'unsaved' | 'unnamed'>('')
const cooNoticeTo = ref('')

/**
 * Who holds a cloned role: a member the company has, by id, or NEW_TEAMMATE for a fresh teammate. A team clone always
 * makes its teammates. It starts on the first member of the destination team, and on a fresh teammate when the team
 * has none; a choice made by hand stays, whatever the team or the member list then does.
 */
const NEW_TEAMMATE = ':new-teammate'
const holder = ref(NEW_TEAMMATE)
let holderTouched = false
const creatingTeammate = computed(() => isTeam.value || holder.value === NEW_TEAMMATE)
/** The company's members, read with the COO each time a template is opened for review; empty until it answers. */
const members = ref<Member[]>([])
const onDestinationTeam = (member: Member) =>
    Boolean(destinationTeamId.value) && (member.teams?.some(team => team.id === destinationTeamId.value) ?? false)
/** Every member the role could go to: those on the destination team first, each group A to Z by what they are called. */
const holderChoices = computed(() =>
    [...members.value].sort(
        (left, right) =>
            Number(onDestinationTeam(right)) - Number(onDestinationTeam(left)) ||
            memberLabel(left).localeCompare(memberLabel(right)),
    ),
)
/** A member as the holder choice names them: what they are called, then the roles they hold. */
function holderLabel(member: Member): string {
    const roles = (member.roles ?? []).map(role => role.name).filter(Boolean)
    return roles.length ? `${memberLabel(member)} · ${roles.join(', ')}` : memberLabel(member)
}
const defaultHolder = () => holderChoices.value.find(onDestinationTeam)?.id ?? NEW_TEAMMATE
function chooseHolder(choice: unknown) {
    holderTouched = true
    holder.value = choice === null || choice === undefined ? NEW_TEAMMATE : String(choice)
}
/** A change of destination team moves an untouched holder choice to that team's first member, or a fresh teammate. */
watch(destinationTeamId, () => {
    if (!holderTouched) holder.value = defaultHolder()
})

/** The blank fields "Pick names for me" would fill: the blank teammates, then the COO's when it is unnamed. */
const blankTeammates = computed(() =>
    creatingTeammate.value
        ? teammateNames.value.flatMap((teammateName, index) => (teammateName.trim() ? [] : [index]))
        : [],
)
const cooBlank = computed(() => cooUnnamed.value && !cooName.value.trim())
const pickCount = computed(() => blankTeammates.value.length + (cooBlank.value ? 1 : 0))
/** True when a name that will be saved is exactly as the picker filled it: `setup.names_auto_assigned`. */
const namesAutoAssigned = computed(
    () =>
        (creatingTeammate.value &&
            teammateNames.value.some(
                (teammateName, index) => teammateName.trim() !== '' && teammateName.trim() === pickedNames.value[index],
            )) ||
        (cooUnnamed.value && cooName.value.trim() !== '' && cooName.value.trim() === cooPicked.value),
)

const ready = computed(
    () =>
        selected.value &&
        !busy.value &&
        !sizeNotInPlan.value &&
        selectedTaskIds.value.length > 0 &&
        Number(money.value) > 0 &&
        (isTeam.value
            ? name.value.trim() && /^[a-z0-9][a-z0-9-]{0,63}$/.test(slug.value)
            : destinationTeamId.value &&
              (creatingTeammate.value || holderChoices.value.some(member => member.id === holder.value))),
)

/** Counts the reviews opened, so an answer that arrives after the form was reset is dropped. */
let reviewOpened = 0
let membersSequence = 0
/**
 * Reads the company's members: the holder choices for a role clone, and the COO among them. Without the list the
 * panel offers no COO naming and no existing holder, and the clone makes a teammate as it always has.
 */
async function loadMembers() {
    const sequence = ++membersSequence
    coo.value = null
    members.value = []
    try {
        const listed = await api.members()
        if (sequence !== membersSequence) return
        members.value = Array.isArray(listed) ? listed : []
        coo.value = members.value.find(member => member.roles?.some(role => role.slug === 'coo')) ?? null
    } catch {
        if (sequence === membersSequence) coo.value = null
    }
    if (sequence === membersSequence && !holderTouched) holder.value = defaultHolder()
}

/** The most names one request may ask for: the API's `MAX_NAME_SUGGESTIONS`. */
const MAX_NAME_SUGGESTIONS = 50
/** How the picker compares a name, as the API leaves names out: whole and by its first word, case-insensitively. */
function nameKeys(value: string): string[] {
    const whole = value.trim().toLowerCase()
    return whole ? [whole, whole.split(/\s+/)[0] ?? whole] : []
}
/** The names the form already holds: the teammates' while they are being made and, while it is unnamed, the COO's. */
const namesInForm = () =>
    [...(creatingTeammate.value ? teammateNames.value : []), cooUnnamed.value ? cooName.value : ''].filter(given =>
        given.trim(),
    )
/**
 * `names` less any the form already holds, each once, in the server's order. The server leaves out only the names of
 * saved members; a name typed or picked into this form is not saved yet, and two teammates given one name would
 * clash on their mail identity and fail the clone.
 */
function unusedNames(names: readonly string[]): string[] {
    const taken = new Set(namesInForm().flatMap(nameKeys))
    return names.filter(candidate => {
        const keys = nameKeys(candidate)
        if (keys.length === 0 || keys.some(at => taken.has(at))) return false
        for (const at of keys) taken.add(at)
        return true
    })
}

/**
 * "Pick names for me": asks the server for one name per blank field, plus one for the COO while it is unnamed and
 * blank, and fills the blanks in order, the COO last. A field typed into meanwhile is never overwritten, and every
 * filled field stays editable. No picked name repeats one already in the form: the server's order is fixed per person,
 * so when its answer holds names the form has, it is asked once more for enough to make up for every filled field.
 */
async function pickNames() {
    const count = pickCount.value
    const review = reviewOpened
    if (!count || picking.value) return
    picking.value = true
    pickError.value = ''
    try {
        const answer = (await api.suggestMemberNames(count))?.names ?? []
        // A template was opened for review again meanwhile: these names were for a form that has been reset.
        if (review !== reviewOpened || !selected.value) return
        let names = unusedNames(answer)
        if (names.length < count && names.length < answer.length) {
            const wider = Math.min(count + namesInForm().length, MAX_NAME_SUGGESTIONS)
            const more = (await api.suggestMemberNames(wider))?.names ?? []
            if (review !== reviewOpened || !selected.value) return
            names = unusedNames([...answer, ...more])
        }
        const filled = [...teammateNames.value]
        const picked = [...pickedNames.value]
        for (const index of blankTeammates.value) {
            const next = names.shift()
            if (!next) break
            filled[index] = next
            picked[index] = next
        }
        teammateNames.value = filled
        pickedNames.value = picked
        const forCoo = cooBlank.value ? names.shift() : undefined
        if (forCoo) {
            cooName.value = forCoo
            cooPicked.value = forCoo
        }
    } catch {
        pickError.value = 'Could not pick names. Type them, or try again.'
    } finally {
        picking.value = false
    }
}

/**
 * Saves what the user gave the COO, after the clone: its name through the member rename, then its avatar through
 * the member avatar upload. Nothing when both were left untouched. A failure never undoes the clone: it is reported,
 * and both can be set on the COO's page.
 */
async function saveCoo(plan: { id: string; name: string; avatar: string | null; failed: boolean } | null) {
    if (!plan) return
    try {
        if (plan.name) {
            await api.patchMember(plan.id, { name: plan.name })
            if (coo.value?.id === plan.id) coo.value = { ...coo.value, name: plan.name, displayName: plan.name }
        }
        if (plan.failed) throw new Error('avatar_not_drawn')
        if (plan.avatar) await api.setMemberAvatar(plan.id, plan.avatar)
    } catch {
        cooNotice.value = 'unsaved'
    }
}

/** What the COO section asks to save, read before the clone while its avatar preview is still on screen. */
async function cooPlan() {
    const member = coo.value
    if (!member || !cooUnnamed.value) return null
    const given = cooName.value.trim()
    if (!given && !avatarRoll.value) return null
    try {
        return { id: member.id, name: given, avatar: (await cooAvatar.value?.toPngDataUrl()) || null, failed: false }
    } catch {
        return { id: member.id, name: given, avatar: null, failed: true }
    }
}

let loadSequence = 0
async function load() {
    const sequence = ++loadSequence
    loading.value = true
    error.value = ''
    try {
        const query = {
            page: page.value,
            limit: 10,
            search: props.searchQuery ?? search.value,
            sort: 'name',
            ...props.filterQuery,
        }
        const result = isTeam.value ? await api.catalogTeams(query) : await api.catalogRoles(query)
        if (sequence !== loadSequence) return
        const listed = asListResponse<CatalogRole | CatalogTeam>(result, query)
        entries.value = listed.data
        pages.value = listed.pages
    } catch {
        if (sequence !== loadSequence) return
        error.value = `Could not load ${props.kind} templates. Try again.`
    } finally {
        if (sequence === loadSequence) loading.value = false
    }
}
/**
 * The daily cap starts at the enterprise's Day $ (#1751, CTO decisions 10 and 13), read through the Spend snapshot the
 * portal already has each time a template is opened for review, so the cap saved is the cap shown, as every other new
 * team and teammate inherits it. It stays at $10 while the read is under way, when the enterprise has no cap row or
 * no Day $, and when the read fails; a Day $ outside the field's bounds starts at the nearest bound. A value the user
 * has typed is never overwritten.
 *
 * Each review starts back at $10: the panel stays mounted between reviews, so an earlier review's Day $ would
 * otherwise stay in the field while this read is under way, or after it fails or finds none.
 */
let capSequence = 0
async function prefillMoney() {
    const sequence = ++capSequence
    if (!moneyTyped.value) money.value = FALLBACK_DAILY_CAP_USD
    let cents: number | null | undefined
    try {
        cents = (await api.spend()).caps.find(cap => cap.layer === 'enterprise')?.moneyCentsDay
    } catch {
        return
    }
    if (sequence !== capSequence || moneyTyped.value || cents == null) return
    money.value = Math.min(MAX_DAILY_CAP_USD, Math.max(MIN_DAILY_CAP_USD, cents / 100))
}
/**
 * The company's AI-account connections, for the expected monthly cost (#1723): undefined while the read is out, null
 * when it failed. Read each time a template is opened for review, as the Day $ is, so it is never an earlier review's.
 * Every cloned teammate runs on the company's account (CTO decision 8), so the estimate is always the shared one: the
 * team's teammates under the team's single daily cap, or the one teammate a role clone makes.
 */
const connections = ref<AgentConnection[] | null | undefined>(undefined)
let connectionsSequence = 0
async function loadConnections() {
    const sequence = ++connectionsSequence
    connections.value = undefined
    try {
        const answer = await api.agentConnections()
        if (sequence === connectionsSequence) {
            connections.value = Array.isArray(answer?.connections) ? answer.connections : null
        }
    } catch {
        if (sequence === connectionsSequence) connections.value = null
    }
}
const monthlyCost = computed(() =>
    connections.value === undefined
        ? []
        : monthlyCostLines({
              connections: connections.value,
              aiAccount: 'shared',
              teammates: teammateNames.value.length,
              dailyCapUsd: money.value,
          }),
)
onMounted(load)
/**
 * The Catalog keeps this panel alive while another template type is shown, so mounting alone would leave a template's
 * off state as it was first read (#1811). Read again each time it is shown after being hidden; the first activation
 * comes with the mount, which has already read.
 */
let hidden = false
onDeactivated(() => {
    hidden = true
})
onActivated(() => {
    if (!hidden) return
    hidden = false
    void load()
})
watch(
    () => JSON.stringify([props.searchQuery ?? null, props.filterQuery ?? {}]),
    () => {
        page.value = 1
        if (!busy.value) selected.value = null
        void load()
    },
)

function choose(entry: CatalogRole | CatalogTeam) {
    reviewOpened++
    selected.value = entry
    name.value = entry.name
    slug.value = entry.slug
    // A role clone leaves out a task the company already holds; a team clone keeps it, and the clone reuses it.
    selectedTaskIds.value = entry.tasks
        .filter(
            task => task.origin !== 'os' && task.status !== 'deprecated' && ('teammates' in entry || !heldByMe(task)),
        )
        .map(task => task.id)
    syncing.value = {}
    syncNotes.value = {}
    // The team chosen last stays while it exists; with no team at all the review opens on "Create a team…".
    if (!teamChoices.value.some(team => team.id === teamId.value)) teamId.value = teamChoices.value[0]?.id ?? NEW_TEAM
    newTeamName.value = ''
    newTeamError.value = ''
    holderTouched = false
    holder.value = defaultHolder()
    // Blank: a teammate left unnamed is cloned with no name and shown by its role title, its placeholder (#1697 O15).
    teammateNames.value = 'teammates' in entry ? entry.teammates.map(() => '') : ['']
    pickedNames.value = []
    pickError.value = ''
    cooName.value = ''
    cooPicked.value = ''
    avatarRoll.value = 0
    cooNotice.value = ''
    status.value = ''
    error.value = ''
    upgrade.value = null
    sizeNotInPlan.value = 'teammates' in entry ? shortfall(entry.planSize) : null
    swapChoices.value = {}
    slotChoices.value = {}
    connecting.clear()
    connectOutcome.value = {}
    scheduleAllowance.value = null
    startSchedules()
    void loadOwner()
    void loadToolReview()
    void prefillMoney()
    void loadMembers()
    void loadConnections()
}

let reviewSequence = 0
/**
 * Opens the review of the chosen template; for a role, on the destination team chosen now. `again` re-reads it for
 * the same form after a Connect (#1843): what is on screen stays until the answer comes, and the schedule rows are
 * left as the person set them. True only when this read answered and is what the screen now shows; false when there
 * is no template, a newer read took over, or the read failed.
 */
async function loadToolReview(again = false): Promise<boolean> {
    const entry = selected.value
    const sequence = ++reviewSequence
    if (!again) {
        toolReview.value = []
        heldRoles.value = null
        reviewTools.value = []
        reviewSlots.value = []
        reviewState.value = 'loading'
    }
    if (!entry) return false
    reviewedTeamId = isTeam.value ? undefined : destinationTeamId.value || undefined
    try {
        const review: CatalogTeamCloneReview = isTeam.value
            ? await api.catalogTeamReview(entry.id)
            : await api.catalogRoleReview(entry.id, reviewedTeamId)
        if (sequence !== reviewSequence) return false
        toolReview.value = Array.isArray(review?.tasks) ? review.tasks : []
        // A team review also says which of the template's roles the company already has (#1842).
        if (isTeam.value && Array.isArray(review?.roles)) heldRoles.value = review.roles
        reviewTools.value = Array.isArray(review?.tools) ? review.tools : []
        reviewSlots.value = Array.isArray(review?.slots) ? review.slots : []
        // The review reads the plan as it is now (#396), so its size report replaces the list's.
        if (isTeam.value && review?.planSize !== undefined) sizeNotInPlan.value = shortfall(review.planSize)
        reviewState.value = 'loaded'
        // A team review also reports the plan's schedule allowance (#1840): the rows start again against it.
        if (!again && isTeam.value && review?.schedules) {
            scheduleAllowance.value = review.schedules
            startSchedules()
        }
        return true
    } catch {
        // The clone never waits on the review: it still works, and the tools can be connected later. A re-read that
        // fails keeps what the last answer showed.
        if (sequence !== reviewSequence || again) return false
        toolReview.value = []
        reviewTools.value = []
        reviewSlots.value = []
        reviewState.value = 'failed'
        return false
    }
}

/**
 * The tools the selected tasks need (#1843), from the review: one row per tool type, naming the selected tasks that
 * need it. A task whose tool is swapped for another no longer counts toward it. Missing tools (not connected) come
 * first, then connected ones; A to Z within each, every vendor alike.
 */
const toolRows = computed(() =>
    reviewTools.value
        .flatMap(tool => {
            const taskIds = tool.taskIds.filter(
                taskId => selectedTaskIds.value.includes(taskId) && !choiceOf(taskId, tool.toolType).startsWith(USE),
            )
            return taskIds.length ? [{ ...tool, taskIds }] : []
        })
        .sort((a, b) => toolRank(a) - toolRank(b) || a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })),
)
/** Missing before connected, as Team setup lists them. */
function toolRank(tool: CatalogReviewTool): number {
    return tool.status === 'connected' ? 1 : 0
}
/** The rows still missing for the team: every one the company has not connected. */
const missingTools = computed(() => toolRows.value.filter(tool => tool.status !== 'connected'))
/** The section shows while the check runs or has failed, and once it has answered with at least one tool needed. */
const toolsSectionShown = computed(() => reviewState.value !== 'loaded' || toolRows.value.length > 0)
/** Only "Connected" or "Not connected", the same for every reader (#1843 decision 16; a personal status is #2264's). */
function toolStatusWords(toolStatus: string): string {
    return toolStatus === 'connected' ? 'Connected' : 'Not connected'
}
/** A tool type by its display name: the review's name for it, else the same plain name the Tools page uses. */
function toolName(toolType: string): string {
    return reviewTools.value.find(tool => tool.toolType === toolType)?.name ?? toolLabel(toolType, 'standard')
}
function taskName(taskId: string): string {
    const task = selectedTasks.value.find(candidate => candidate.id === taskId)
    return task ? task.name || task.slug : taskId
}
/** `A`, `A and B`, `A, B and C`. */
function inWords(names: readonly string[]): string {
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
}
/**
 * A task's tools as it lists them: by display name once the review names them, else by the name each entry declares,
 * whatever shape it was stored in (a string, or an object naming the tool), as every other tool list reads them.
 */
function taskTools(task: CatalogTask): string {
    const named = reviewTools.value.filter(tool => tool.taskIds.includes(task.id)).map(tool => tool.name)
    const declared = named.length ? named : toolEntryNames(task.currentVersion?.tools)
    return declared.length ? declared.join(', ') : 'No tools needed'
}
/**
 * The line above Clone for each missing tool: the selected tasks that need it can't do their work until it is
 * connected for the company. It never promises a run, and cloning stays allowed.
 */
const missingToolLines = computed(() =>
    missingTools.value.map(tool => {
        const names = tool.taskIds.map(taskName)
        return `${inWords(names)} won't be able to do ${names.length === 1 ? 'its' : 'their'} work until ${tool.name} is connected for your company. You can connect it now or after cloning.`
    }),
)

/**
 * Whether the reader may connect a tool for the whole company (#1843, John's decision of 13 Oct): only the owner's
 * connection is saved company-wide, so only an owner is offered Connect. Read once, when a template is first opened
 * for review; anyone it cannot be read for is asked to ask an owner instead.
 */
const isOwner = ref(false)
let ownerRead = false
async function loadOwner() {
    if (ownerRead) return
    ownerRead = true
    try {
        isOwner.value = (await api.me())?.isOwner === true
    } catch {
        isOwner.value = false
    }
}
/** The tool types Connect was pressed for, read again when the person comes back to this tab. */
const connecting = new Set<string>()
/** What the re-read after a Connect found for each tool type: connected now, or still missing. */
const connectOutcome = ref<Record<string, 'connected' | 'missing'>>({})
function startConnect(toolType: string) {
    connecting.add(toolType)
}
/**
 * The person is back on the clone tab after a Connect opened the Tools page in another: the review is read again.
 * When that re-read does not answer (it failed, or a newer read took over), nothing is said about the tools pressed:
 * the screen cannot tell whether sign-in finished, so they stay waiting and the next return to the tab reads again.
 */
async function backFromConnect() {
    if (!connecting.size || !selected.value || document.visibilityState === 'hidden') return
    const pressed = [...connecting]
    const opened = reviewOpened
    connecting.clear()
    if (!(await loadToolReview(true))) {
        // Unless another template was opened meanwhile, which starts its own Connects over.
        if (opened === reviewOpened) for (const toolType of pressed) connecting.add(toolType)
        return
    }
    const outcome = { ...connectOutcome.value }
    for (const toolType of pressed) {
        const tool = reviewTools.value.find(candidate => candidate.toolType === toolType)
        outcome[toolType] = tool?.status === 'connected' ? 'connected' : 'missing'
    }
    connectOutcome.value = outcome
}
function onReturn() {
    void backFromConnect()
}
onMounted(() => {
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onReturn)
})
onBeforeUnmount(() => {
    window.removeEventListener('focus', onReturn)
    document.removeEventListener('visibilitychange', onReturn)
})
watch(destinationTeamId, next => {
    if (selected.value && !isTeam.value && (next || undefined) !== reviewedTeamId) void loadToolReview()
})

const reviewOf = (taskId: string) => toolReview.value.find(row => row.taskId === taskId)
/**
 * What a team clone finds in the company and what it adds (#1842), from the team review: a role or task the company
 * already has is reused as it is, since a role is one job across the company and a task belongs to its role, not a
 * team. What the clone adds is its teammates, every role the company lacks, and the ticked tasks it lacks. Null for a
 * role clone, and until the review says what the company holds.
 */
/** Who holds a role the company already has: nothing to add when someone on this team does. */
function heldBy(fact: CatalogHeldRole): string {
    if (fact.holdersOnTeam > 0) return ''
    if (fact.holders > 0)
        return ' Its holders elsewhere keep running its tasks; this team runs them once a holder sits on it.'
    return ' Nobody holds it yet.'
}

const cloneContents = computed(() => {
    const entry = selected.value
    const held = heldRoles.value
    if (!isTeam.value || !entry || !('teammates' in entry) || !held) return null
    const roleName = (fact: CatalogHeldRole) => entry.roles.find(role => role.id === fact.roleId)?.name || fact.slug
    const kept = selectedTasks.value.filter(task => selectedTaskIds.value.includes(task.id))
    const taskLabel = (task: CatalogTask) => task.name || task.slug
    const existing = [
        ...held.flatMap(fact => {
            if (!fact.exists) return []
            return [
                `${roleName(fact)} role: your company already has it, so this team uses it as it is.${heldBy(fact)}`,
            ]
        }),
        ...kept.flatMap(task => {
            const fact = reviewOf(task.id)
            if (!fact?.exists) return []
            return [`${taskLabel(task)}: your company already has it, so this team uses it as it is.`]
        }),
    ]
    return {
        existing,
        roles: held.filter(fact => !fact.exists).map(roleName),
        tasks: kept.filter(task => !reviewOf(task.id)?.exists).map(taskLabel),
        teammates: entry.teammates.length,
    }
})
const missingOf = (taskId: string) => reviewOf(taskId)?.missing ?? []
const candidatesOf = (taskId: string) => reviewOf(taskId)?.candidates ?? []
const swapKey = (taskId: string, tool: string) => `${taskId}\n${tool}`
/**
 * A task left out is dropped. Otherwise its choice, or Keep when it has none, its tool is no longer missing (a re-read
 * after Connect, or another destination team, found it connected) or its stand-in can no longer stand in.
 */
function choiceOf(taskId: string, tool: string) {
    if (!selectedTaskIds.value.includes(taskId)) return DROP
    const chosen = swapChoices.value[swapKey(taskId, tool)]
    return chosen?.startsWith(USE) &&
        missingOf(taskId).includes(tool) &&
        candidatesOf(taskId).includes(chosen.slice(USE.length))
        ? chosen
        : KEEP
}
/** Drop is the same as unticking the task. Any other choice keeps the task selected. */
function setChoice(task: CatalogTask, tool: string, value: unknown) {
    const choice = typeof value === 'string' ? value : KEEP
    if (choice === DROP) {
        selectTask(task, false)
        return
    }
    if (!selectedTaskIds.value.includes(task.id)) selectTask(task, true)
    swapChoices.value = { ...swapChoices.value, [swapKey(task.id, tool)]: choice }
}
/** The chosen swaps of the tasks still selected, in task order. Keep and Drop send none. */
function toolSwaps(): CatalogToolSwap[] {
    return selectedTaskIds.value.flatMap(taskId =>
        missingOf(taskId).flatMap(tool => {
            const choice = choiceOf(taskId, tool)
            return choice.startsWith(USE) ? [{ taskId, from: tool, to: choice.slice(USE.length) }] : []
        }),
    )
}
/**
 * The slots the selected tasks declare, each naming only the selected tasks that need it: a slot no selected task
 * declares is not asked about, and the API refuses a fill for one.
 */
const slotRows = computed(() =>
    reviewSlots.value.flatMap(slot => {
        const taskIds = slot.taskIds.filter(taskId => selectedTaskIds.value.includes(taskId))
        return taskIds.length ? [{ ...slot, taskIds }] : []
    }),
)
/** The connections a slot may be filled with, the one already filling it among them. */
function slotOptions(slot: CatalogReviewSlot) {
    const { filledBy, candidates } = slot
    return filledBy && !candidates.some(option => option.bindingId === filledBy.bindingId)
        ? [filledBy, ...candidates]
        : candidates
}
/** The connection chosen for a slot: the person's pick while it is still offered, else the one already filling it. */
function slotChoice(slot: CatalogReviewSlot): string {
    const chosen = slotChoices.value[slot.key]
    if (chosen && slotOptions(slot).some(option => option.bindingId === chosen)) return chosen
    return slot.filledBy?.bindingId ?? NO_FILL
}
/** #957: the connections offered for a slot that leave some of its declared actions unmapped, with those actions. */
function unmappedOptions(slot: CatalogReviewSlot): Array<CatalogSlotBinding & { missingActions: string[] }> {
    return slotOptions(slot).flatMap(option =>
        option.missingActions?.length ? [{ ...option, missingActions: option.missingActions }] : [],
    )
}
/** The chosen connection, when it leaves actions unmapped: a warning, never a refusal, and the clone is unchanged. */
function chosenUnmapped(slot: CatalogReviewSlot) {
    const chosen = slotChoice(slot)
    return unmappedOptions(slot).find(option => option.bindingId === chosen)
}
/** The connection's editor on Tools, opened at its Actions section. */
function actionsLink(bindingId: string): string {
    return `/tools?actions=${encodeURIComponent(bindingId)}`
}
function chooseSlot(slot: CatalogReviewSlot, value: unknown) {
    slotChoices.value = { ...slotChoices.value, [slot.key]: typeof value === 'string' ? value : NO_FILL }
}
/** The fills to send: each slot whose chosen connection is not the one already filling it. */
function slotFills(): CatalogSlotFill[] {
    return slotRows.value.flatMap(slot => {
        const bindingId = slotChoice(slot)
        return bindingId !== NO_FILL && bindingId !== slot.filledBy?.bindingId ? [{ slot: slot.key, bindingId }] : []
    })
}
/** Ticking or unticking a task starts its tool choices over, so ticking it again returns them to Keep. */
function selectTask(task: CatalogTask, checked: boolean | 'indeterminate') {
    selectedTaskIds.value =
        checked === true ? [...selectedTaskIds.value, task.id] : selectedTaskIds.value.filter(id => id !== task.id)
    swapChoices.value = Object.fromEntries(
        Object.entries(swapChoices.value).filter(([key]) => !key.startsWith(`${task.id}\n`)),
    )
    // A task ticked again brings its schedule row back as it was, unless the plan has no room left for it on.
    const schedule = scheduleChoices.value[task.id]
    if (checked === true && schedule?.enabled && !scheduleRoom(task.id)) {
        scheduleChoices.value = { ...scheduleChoices.value, [task.id]: { ...schedule, enabled: false } }
    }
}
function approvals(task: CatalogTask) {
    return buildGateBadges(task.currentVersion?.gates)
        .map(gate => `${gate.owner} · ${gate.approval}`)
        .join(', ')
}
async function adopt() {
    if (!ready.value || !selected.value) return
    // #1796: a clone adds teammates that run on the AI account, so without one nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    busy.value = true
    status.value = `Cloning ${selected.value.name}…`
    error.value = ''
    upgrade.value = null
    cooNotice.value = ''
    kpiLeft.value = null
    try {
        // Sent only when a swap was chosen, so a clone with none is the same request it always was.
        const swaps = toolSwaps()
        const withSwaps = swaps.length ? { toolSwaps: swaps } : {}
        // Likewise sent only when a slot is to be filled by a connection other than the one filling it now.
        const fills = slotFills()
        const withFills = fills.length ? { slotFills: fills } : {}
        // Likewise sent only when a kept name came from "Pick names for me" (`setup.names_auto_assigned`).
        const picked = namesAutoAssigned.value ? { namesAutoAssigned: true } : {}
        const forCoo = await cooPlan()
        if (isTeam.value) {
            const result = await api.cloneCatalogTeam(selected.value.id, {
                name: name.value.trim(),
                slug: slug.value,
                teammateNames: teammateNames.value.map(teammateName => teammateName.trim()),
                taskIds: selectedTaskIds.value,
                moneyCentsDay: Math.round(Number(money.value) * 100),
                ...withSwaps,
                ...withFills,
                ...picked,
                // Sent only for a template that suggests schedules, so a clone of one that suggests none is unchanged.
                ...scheduleBody(),
            })
            openKpiStep(result)
        } else {
            await api.cloneCatalogRole(selected.value.id, {
                teamId: destinationTeamId.value,
                // A fresh teammate is named and capped here; an existing member keeps their name and cap.
                ...(creatingTeammate.value
                    ? {
                          createTeammate: true,
                          teammateName: teammateNames.value[0]?.trim() ?? '',
                          moneyCentsDay: Math.round(Number(money.value) * 100),
                      }
                    : { memberId: holder.value }),
                taskIds: selectedTaskIds.value,
                ...withSwaps,
                ...withFills,
                ...picked,
            })
        }
        const given = creatingTeammate.value ? null : holderChoices.value.find(member => member.id === holder.value)
        // Names exactly the tools the list showed as missing for the tasks cloned (#1843), or none.
        status.value = [
            given
                ? `${selected.value.name} cloned and given to ${memberLabel(given)}.`
                : `${selected.value.name} cloned.`,
            ...(reviewState.value === 'loaded' ? missingTools.value : []).map(
                tool =>
                    `Connect ${tool.name} for your company so ${inWords(tool.taskIds.map(taskName))} can do their work.`,
            ),
        ].join(' ')
        // The clone stands whatever becomes of the COO's name and avatar.
        await saveCoo(forCoo)
        if (!cooNotice.value && cooUnnamed.value) cooNotice.value = 'unnamed'
        cooNoticeTo.value = coo.value ? `/members/${coo.value.id}` : ''
        selected.value = null
        emit('adopted')
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        status.value = ''
        // The code chooses the refusal first (#449), then the plan unit it names (#1840); a 402 that names neither is
        // the member limit, as it always was. A size the plan does not include (os#1624, a list read before the plan
        // changed) is shown in the server's own sentence, the one the form shows.
        if (isTeamSizeRefusal(caught)) upgrade.value = 'size'
        else if (isCraftedRefusal(caught)) upgrade.value = 'crafted'
        else if (refusalUnit(caught) === 'schedule') upgrade.value = 'schedules'
        else if (requestFailure(caught)?.status === 402 || code === 'plan_limit_reached') upgrade.value = 'members'
        // Only a sentence is shown as the server's words: a bare code is never put in front of a person.
        if (upgrade.value === 'size')
            error.value = code.includes(' ')
                ? code
                : 'Your plan does not include a team of this size. Upgrade to add it.'
        else if (upgrade.value === 'crafted') error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else if (upgrade.value === 'schedules')
            error.value =
                'This clone would switch on more schedules than your plan allows. Switch some off, or upgrade for more.'
        else if (upgrade.value === 'members')
            error.value = 'This clone needs more teammates than your plan allows. Upgrade to add them.'
        else if (/slug|already_cloned/.test(code))
            error.value =
                'Your company already has a team, role or task with this identity. Choose another team name, or use the roles and tasks you already have.'
        else if (code === 'missing_catalog_dependencies')
            error.value = 'Some selected tasks hand off to tasks you removed. Keep those tasks and try again.'
        else if (code === 'invalid_tool_swap')
            error.value =
                'A tool you chose to use instead is no longer connected. Choose again or keep the task and connect its tool after cloning.'
        else if (code === 'member_email_taken')
            error.value = 'A teammate with this name already exists. Choose another name and retry.'
        else if (code === 'member_not_found')
            error.value = 'That teammate is no longer in your company. Choose another and retry.'
        else error.value = 'Could not clone. Your choices have been kept; try again.'
    } finally {
        busy.value = false
    }
}

/**
 * The KPI step after a team clone (#1839): every KPI the clone created (the team's, its roles' and its tasks'), none
 * signed off, with one request that signs them all off after any change to a team or role target or band. A task
 * KPI's target belongs to its task's definition, so it is shown here and changed there. A template with no KPIs has no
 * step, and leaving the step signs nothing: each KPI waits for a person to sign it off.
 */
type ClonedTeam = Awaited<ReturnType<typeof api.cloneCatalogTeam>>
type KpiStep = {
    teamId: string
    teamName: string
    kpis: CatalogClonedKpi[]
    owners: Record<string, string>
    /** The roles the clone reused because the company already had them: none of the template's role KPIs lands on them. */
    reusedRoles: string[]
}
const CLONE_STEPS = [
    { id: 'clone', label: 'Clone' },
    { id: 'kpis', label: 'Sign off KPIs' },
] as const
const LAYER_SHAPE = { team: 'circle', role: 'hexagon', task: 'square' } as const
const LAYER_WORD = { team: 'Team KPI', role: 'Role KPI', task: 'Task KPI' } as const
const kpiStep = ref<KpiStep | null>(null)
/** What each team and role KPI's target and band fields hold, by KPI id, as the text the fields hand back. */
const kpiDrafts = ref<Record<string, { target: string; band: string }>>({})
const kpiSignedOff = ref<string[]>([])
const kpiBusy = ref(false)
const kpiError = ref('')
const kpiStatus = ref('')
/** Whether a sign-off has gone through in this step: after one, the button sends again only once a field changes. */
const kpiSent = ref(false)
/** The cloned team whose KPIs the person left the step on, so the panel can say where to finish. */
const kpiLeft = ref<{ teamId: string; teamName: string } | null>(null)

/** The team KPIs the selected team template carries, which tell the clone form a sign-off step follows. */
const templateKpiCount = computed(() => {
    const template = selected.value
    return isTeam.value && template && 'kpis' in template ? (template.kpis?.length ?? 0) : 0
})

/** The KPIs that can be signed off here: a KPI with no source stays unsigned until one is set. */
const signableKpis = computed(() => kpiStep.value?.kpis.filter(kpi => kpi.sourceRef).length ?? 0)
const unsourcedKpis = computed(() => kpiStep.value?.kpis.filter(kpi => !kpi.sourceRef).length ?? 0)

/** The KPIs whose target or band field holds something that is not a number, so each row can say so. */
const invalidKpis = computed(() =>
    Object.entries(kpiDrafts.value)
        .filter(([, draft]) => {
            const band = numberOf(draft.band)
            return numberOf(draft.target) === undefined || band === undefined || (band !== null && band < 0)
        })
        .map(([id]) => id),
)

/** Whether Sign off all would send anything new: always before the first sign-off, then only after a change. */
const kpiSendable = computed(() => {
    const step = kpiStep.value
    if (!step) return false
    if (!kpiSent.value) return true
    const edits = kpiEdits(step)
    return edits === null || edits.length > 0
})

function openKpiStep(result: ClonedTeam) {
    const kpis = result.kpis ?? []
    kpiError.value = ''
    kpiStatus.value = ''
    kpiSignedOff.value = []
    kpiSent.value = false
    kpiLeft.value = null
    if (kpis.length === 0) {
        kpiStep.value = null
        kpiDrafts.value = {}
        return
    }
    const owners: Record<string, string> = { [result.team.id]: result.team.name }
    for (const role of result.roles) owners[role.id] = role.name
    for (const task of result.tasks) owners[task.id] = task.name || task.slug
    const reused = new Set(result.reusedRoleIds ?? [])
    const reusedRoles = result.roles.filter(role => reused.has(role.id)).map(role => role.name)
    kpiStep.value = { teamId: result.team.id, teamName: result.team.name, kpis, owners, reusedRoles }
    kpiDrafts.value = Object.fromEntries(
        kpis
            .filter(editableKpi)
            .map(kpi => [kpi.id, { target: numberText(kpi.targetValue), band: numberText(kpi.amberBand) }]),
    )
}

/** A team or role KPI with a direction has its target and band set here; a task KPI's are its task's. */
function editableKpi(kpi: CatalogClonedKpi): boolean {
    return kpi.layer !== 'task' && kpi.targetDirection !== null
}

function numberText(value: number | null): string {
    return value === null ? '' : String(value)
}

/** A field's text as a number: none when it is empty, undefined when it is not a number. */
function numberOf(text: string): number | null | undefined {
    const trimmed = text.trim()
    if (trimmed === '') return null
    const value = Number(trimmed)
    return Number.isFinite(value) ? value : undefined
}

function setKpiDraft(id: string, field: 'target' | 'band', value: string | number) {
    const draft = kpiDrafts.value[id]
    if (draft) kpiDrafts.value = { ...kpiDrafts.value, [id]: { ...draft, [field]: String(value) } }
}

/** `kpi` with what its fields hold, for the line that reads its target and band back. */
function shownKpi(kpi: CatalogClonedKpi): CatalogClonedKpi {
    const draft = kpiDrafts.value[kpi.id]
    if (!draft) return kpi
    const targetValue = numberOf(draft.target)
    const amberBand = numberOf(draft.band)
    return {
        ...kpi,
        targetValue: targetValue === undefined ? kpi.targetValue : targetValue,
        amberBand: amberBand === undefined ? kpi.amberBand : amberBand,
        amberBandType: kpi.amberBandType ?? 'absolute',
    }
}

/** The team and role KPIs whose fields changed, or null when a field holds something that is not a number. */
function kpiEdits(step: KpiStep): TeamKpiSignOffEdit[] | null {
    const edits: TeamKpiSignOffEdit[] = []
    for (const kpi of step.kpis) {
        const draft = kpiDrafts.value[kpi.id]
        if (!draft) continue
        const targetValue = numberOf(draft.target)
        const amberBand = numberOf(draft.band)
        if (targetValue === undefined || amberBand === undefined || (amberBand !== null && amberBand < 0)) return null
        if (targetValue === kpi.targetValue && amberBand === kpi.amberBand) continue
        // A band set where there was none is a distance from the target, until its type is changed on the team's KPIs.
        const bandType = amberBand !== null && !kpi.amberBandType ? { amberBandType: 'absolute' as const } : {}
        edits.push({ id: kpi.id, targetValue, amberBand, ...bandType })
    }
    return edits
}

/** What a refused sign-off says: the server's own sentence when it sent one, never a bare code. */
function kpiRefusal(caught: unknown): string {
    const message = caught instanceof Error ? caught.message : ''
    if (/\s/.test(message)) return message
    if (requestFailure(caught)?.status === 403)
        return 'Only a person signed in to their own session can sign these KPIs off.'
    if (message === 'invalid_kpis') return 'A target or AMBER band is not valid. Check the fields and try again.'
    return 'Could not sign the KPIs off. Your changes are kept; try again.'
}

async function signOffAll() {
    const step = kpiStep.value
    if (!step || kpiBusy.value) return
    const edits = kpiEdits(step)
    if (!edits) {
        kpiError.value = 'Enter each target and AMBER band as a number, or leave it empty.'
        return
    }
    kpiBusy.value = true
    kpiError.value = ''
    kpiStatus.value = ''
    try {
        const result = await api.signOffTeamKpis(step.teamId, { edits })
        // The server answers only what this request signed: a KPI an earlier click signed and this one left as it was
        // keeps its sign-off, so it stays signed here too. An edited KPI is signed or not as the server says.
        const edited = new Set(edits.map(edit => edit.id))
        const signed = new Set([...kpiSignedOff.value.filter(id => !edited.has(id)), ...result.signedOff])
        kpiSignedOff.value = [...signed]
        // What was sent is what is stored now, so each row reads its new target and band.
        step.kpis = step.kpis.map(kpi => {
            const edit = edits.find(each => each.id === kpi.id)
            return edit ? { ...kpi, ...edit } : kpi
        })
        kpiSent.value = true
        kpiStatus.value = signOffResult(step, signed)
    } catch (caught) {
        kpiError.value = kpiRefusal(caught)
    } finally {
        kpiBusy.value = false
    }
}

/**
 * What a sign-off did, with the true reason for each KPI it left unsigned: one with no source waits for a source, and
 * one with a source that the server still left (a task KPI its task no longer holds, say) is finished on the team page.
 */
function signOffResult(step: KpiStep, signed: ReadonlySet<string>): string {
    const left = step.kpis.filter(kpi => !signed.has(kpi.id))
    if (left.length === 0) return 'Every KPI is signed off. Each source task can record its readings now.'
    const unsourced = left.filter(kpi => !kpi.sourceRef).length
    const sourced = left.length - unsourced
    return [
        `${signed.size} signed off.`,
        unsourced ? `${unsourced} without a source stay unsigned until one is set.` : '',
        sourced
            ? `${sourced} with a source could not be signed off here: open the team's page to check ${sourced === 1 ? 'it' : 'them'}.`
            : '',
    ]
        .filter(Boolean)
        .join(' ')
}

/** Leaves the step. The panel then says whether the team's KPIs still wait for a sign-off, and links to the team. */
function closeKpiStep() {
    const step = kpiStep.value
    if (step) {
        const unsigned = step.kpis.filter(kpi => !kpiSignedOff.value.includes(kpi.id)).length
        kpiLeft.value = { teamId: step.teamId, teamName: step.teamName }
        const kpiLine =
            unsigned === 0
                ? `${step.teamName}'s KPIs are signed off. You can see and change them on the team's page.`
                : `${unsigned} of ${step.teamName}'s ${step.kpis.length} KPIs still need a sign-off, and none of those records readings until it has one. Set their sources and sign them off on the team's page.`
        // Kept beside the clone's own line, which says to connect its tools.
        status.value = [status.value, kpiLine].filter(Boolean).join(' ')
    }
    kpiStep.value = null
    kpiDrafts.value = {}
    kpiError.value = ''
    kpiStatus.value = ''
}

/** Why a row has no target or band fields, or nothing when it has them. */
function kpiFieldsNote(kpi: CatalogClonedKpi): string {
    if (editableKpi(kpi)) return ''
    if (kpi.layer === 'task') return "Its target belongs to its task, so it is changed on the task's page, not here."
    return "No target yet, so there is nothing to change here. Set one on the team's page."
}

/** Where a KPI's readings come from, or why it has no source yet. */
function kpiSourceLine(kpi: CatalogClonedKpi): string {
    if (kpi.sourceKind && kpi.sourceRef) return `Source: ${kpi.sourceName ?? 'a task of this team'}`
    if (kpi.sourceNotCloned) return `No source: ${kpi.sourceNotCloned} was not cloned`
    return 'No source yet: set one before it can be signed off'
}
</script>

<template>
    <ZhPanel as="article" class="space-y-3" :aria-label="`Clone ${kind}`">
        <ZhSectionHeading>{{ isTeam ? 'Clone Team' : 'Clone Role' }}</ZhSectionHeading>
        <p class="text-muted-foreground text-sm">{{ isTeam ? 'Create a team with fresh teammates, roles and tasks.' : 'Add a fresh teammate and their tasks to a team.' }} Credentials and memory stay with their owner.</p>
        <p role="status" aria-live="polite" class="text-sm">{{ status || (loading ? 'Loading templates…' : '') }}</p>
        <p v-if="cooNotice" :role="cooNotice === 'unsaved' ? 'alert' : undefined" class="text-sm" :class="cooNotice === 'unsaved' ? 'text-destructive' : 'text-muted-foreground'">
            {{ cooNotice === 'unsaved' ? "Your COO's name or avatar did not save. You can set them on the" : 'Your COO has no name yet. You can name it and set its photo on the' }}
            <RouterLink v-if="cooNoticeTo" :to="cooNoticeTo">COO's page</RouterLink><template v-else>COO's page</template>.
        </p>
        <p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
        <RouterLink v-if="upgrade === 'members' || upgrade === 'size'" to="/billing#plans">Upgrade to add</RouterLink>
        <RouterLink v-else-if="upgrade === 'schedules' || upgrade === 'crafted'" to="/billing#plans">See plans on Billing</RouterLink>
        <Button v-if="error && !selected" variant="outline" :disabled="loading" @click="load">Retry</Button>
        <!-- #1839: after leaving the KPI step, where the cloned team's KPIs can be finished. -->
        <RouterLink v-if="kpiLeft && !kpiStep" :to="`/teams/${kpiLeft.teamId}`">Open {{ kpiLeft.teamName }}'s page</RouterLink>
        <!-- #1839: the clone's KPIs, signed off in one step. Shown in place of the templates until the person leaves it. -->
        <section v-if="kpiStep" class="space-y-3" aria-labelledby="clone-kpi-step">
            <ZhStepBar :steps="CLONE_STEPS" current="kpis" />
            <ZhSectionHeading id="clone-kpi-step">Sign off {{ kpiStep.teamName }}'s KPIs</ZhSectionHeading>
            <p class="text-muted-foreground text-sm">These KPIs came with the template. None takes a reading until a person signs it off. Change a team or role target or AMBER band if yours differ, then sign them all off at once.</p>
            <ul class="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
                <li>A <span class="font-medium">KPI</span> is a number the team is judged by, such as replies sent each week.</li>
                <li>Its <span class="font-medium">target</span> is the value to reach, or to stay under. Its <span class="font-medium">source</span> is the task whose runs measure it.</li>
                <li>The <span class="font-medium">AMBER band</span> is a margin around the target: a number inside it shows amber, a warning, before it counts as off target.</li>
                <li>A <span class="font-medium">reading</span> is one measurement, and the <span class="font-medium">cadence</span> is how often one is due.</li>
            </ul>
            <p class="text-muted-foreground text-sm">You can also do this later, on {{ kpiStep.teamName }}'s page.</p>
            <p v-for="roleName in kpiStep.reusedRoles" :key="roleName" class="text-muted-foreground text-sm">{{ roleName }} was already in your company, so the clone uses it as it is: a role's KPIs are one target per job, so none are added from the template.</p>
            <p v-if="kpiError" role="alert" class="text-destructive text-sm">{{ kpiError }}</p>
            <p v-if="kpiStatus" role="status" class="text-sm">{{ kpiStatus }}</p>
            <ZhPanel v-for="kpi in kpiStep.kpis" :key="kpi.id" as="article" class="space-y-2 text-sm" :aria-label="kpi.name">
                <div class="flex flex-wrap items-center gap-2">
                    <ZhLayerIcon :shape="LAYER_SHAPE[kpi.layer]" />
                    <span class="text-muted-foreground">{{ LAYER_WORD[kpi.layer] }}<template v-if="kpiStep.owners[kpi.ownerId]"> · {{ kpiStep.owners[kpi.ownerId] }}</template></span>
                    <span class="font-medium">{{ kpi.name }}</span>
                    <ZhSystemLabel v-if="kpi.isKey" as="span" tone="system">· Key</ZhSystemLabel>
                    <ZhStatusPill :tone="kpiSignedOff.includes(kpi.id) ? 'completed' : 'action'" :pulse="false">{{ kpiSignedOff.includes(kpi.id) ? 'Signed off' : 'Not signed off' }}</ZhStatusPill>
                </div>
                <p>Target: {{ kpiTargetText(shownKpi(kpi)) }} · AMBER band: {{ kpiBandText(shownKpi(kpi)) || 'none' }} · {{ kpiCadenceText(kpi) || 'No cadence' }}</p>
                <p :class="kpi.sourceRef ? '' : 'text-muted-foreground'">{{ kpiSourceLine(kpi) }}</p>
                <p v-if="kpiFieldsNote(kpi)" class="text-muted-foreground">{{ kpiFieldsNote(kpi) }}</p>
                <p v-if="invalidKpis.includes(kpi.id)" class="text-destructive">This target or AMBER band is not a number. Enter a number, or leave it empty.</p>
                <div v-if="kpiDrafts[kpi.id]" class="grid gap-2 sm:grid-cols-2">
                    <label class="grid gap-1">Target ({{ kpi.unit || 'value' }})<Input :model-value="kpiDrafts[kpi.id]?.target" inputmode="decimal" :disabled="kpiBusy" @update:model-value="setKpiDraft(kpi.id, 'target', $event)" /></label>
                    <label class="grid gap-1">AMBER band{{ kpi.amberBandType === 'percentage' ? ' (% of target)' : '' }}<Input :model-value="kpiDrafts[kpi.id]?.band" inputmode="decimal" :disabled="kpiBusy" @update:model-value="setKpiDraft(kpi.id, 'band', $event)" /></label>
                </div>
            </ZhPanel>
            <p v-if="unsourcedKpis" class="text-muted-foreground text-sm">{{ unsourcedKpis }} {{ unsourcedKpis === 1 ? 'KPI has' : 'KPIs have' }} no source yet, so Sign off all leaves {{ unsourcedKpis === 1 ? 'it' : 'them' }} unsigned. Set a source on {{ kpiStep.teamName }}'s page, then sign {{ unsourcedKpis === 1 ? 'it' : 'them' }} off there.</p>
            <p v-if="!kpiSent" class="text-muted-foreground text-sm">Sign off all signs off the {{ signableKpis }} {{ signableKpis === 1 ? 'KPI' : 'KPIs' }} with a source, in your name, in one go. You can change a target later on the team's page.</p>
            <div class="flex flex-wrap items-center gap-2">
                <Button type="button" :disabled="kpiBusy || !kpiSendable" @click="signOffAll">{{ kpiBusy ? 'Signing off…' : 'Sign off all' }}</Button>
                <Button type="button" variant="outline" :disabled="kpiBusy" @click="closeKpiStep">{{ kpiSignedOff.length ? 'Done' : 'Later' }}</Button>
                <RouterLink :to="`/teams/${kpiStep.teamId}`" class="text-sm">Open {{ kpiStep.teamName }}'s page</RouterLink>
            </div>
        </section>
        <template v-if="!selected && !error && !kpiStep">
            <form v-if="searchQuery === undefined" class="flex gap-2" @submit.prevent="page = 1; load()">
                <ListSearchField v-model="search" :aria-label="`Search ${kind} templates`" placeholder="Search templates" />
                <Button type="submit" variant="outline" :disabled="loading">Search</Button>
            </form>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <ZhPanel v-for="entry in entries" :key="entry.id" class="flex flex-col items-start gap-2">
                <div class="flex items-center gap-2">
                    <p class="font-medium">{{ entry.name }}</p>
                    <!-- The publisher's own team behind the template is off (#1811). Clone below is untouched. -->
                    <ZhTooltip v-if="'teammates' in entry && entry.enabled === false" :text="OFF_HINT">
                        <ZhSystemLabel as="span" tone="muted" tabindex="0">Off</ZhSystemLabel>
                    </ZhTooltip>
                </div>
                <!-- #1727: one line, cut to fit; the full text is the title. A role's line is built from its task names. -->
                <p v-if="cardLine(entry)" class="text-muted-foreground w-full truncate text-sm" :title="cardLine(entry)">{{ cardLine(entry) }}</p>
                <p v-if="'teammates' in entry" class="text-muted-foreground text-sm">{{ entry.teammates.length }} teammate{{ entry.teammates.length === 1 ? '' : 's' }} · {{ entry.roles.length }} role{{ entry.roles.length === 1 ? '' : 's' }}</p>
                <!-- os#1624: a size the plan does not include is still listed, and says how to get it. -->
                <p v-if="cardSizeLine(entry)" class="text-sm">{{ cardSizeLine(entry) }} <RouterLink to="/billing#plans">Upgrade to add</RouterLink></p>
                <Button class="mt-auto" variant="outline" :disabled="busy || loading" @click="choose(entry)">Review {{ entry.name }}</Button>
            </ZhPanel>
            </div>
            <div v-if="pages > 1" class="flex items-center gap-2">
                <Button variant="outline" :disabled="loading || page <= 1" @click="page--; load()">Previous</Button>
                <span class="text-sm">{{ page }} of {{ pages }}</span>
                <Button variant="outline" :disabled="loading || page >= pages" @click="page++; load()">Next</Button>
            </div>
        </template>
        <ZhEmptyState v-if="!loading && !error && !selected && !kpiStep && !entries.length" :label="narrowed ? `No matching ${kind} templates` : `No ${kind} templates published yet`">{{ narrowed ? 'Try a different search or another template type.' : 'Published templates will appear here when their owner makes them available.' }}</ZhEmptyState>
        <form v-if="selected" class="space-y-3" @submit.prevent="adopt">
            <!-- #1839: a template that carries KPIs shows where its sign-off step comes, from the first step. -->
            <template v-if="templateKpiCount">
                <ZhStepBar :steps="CLONE_STEPS" current="clone" />
                <p class="text-muted-foreground text-sm">This template comes with {{ templateKpiCount }} team {{ templateKpiCount === 1 ? 'KPI' : 'KPIs' }}. After cloning, you check the targets and sign them off.</p>
            </template>
            <!-- os#1624: a team size the plan does not include is explained here, and the clone stays off. -->
            <p v-if="sizeNotInPlan" class="text-sm">{{ teamSizeSentence(sizeNotInPlan) }} <RouterLink to="/billing#plans">Upgrade to add</RouterLink></p>
            <fieldset :disabled="busy" class="space-y-3">
                <p class="font-medium">{{ selected.name }}</p>
                <p v-if="'charter' in selected" class="text-muted-foreground text-sm">{{ selected.charter.purpose }}</p>
                <template v-if="isTeam">
                    <label class="grid gap-1 text-sm">Team name<Input v-model="name" required maxlength="200" /></label>
                    <label class="grid gap-1 text-sm">Team slug<Input v-model="slug" required pattern="[a-z0-9][a-z0-9-]{0,63}" maxlength="64" /></label>
                </template>
                <template v-else>
                    <div class="grid gap-1 text-sm">
                        <span :id="`${kind}-destination-team`">Add to team</span>
                        <Select v-model="teamId" :disabled="busy">
                            <SelectTrigger :aria-labelledby="`${kind}-destination-team`"><SelectValue placeholder="Select a team" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="team in teamChoices" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
                                <SelectSeparator v-if="teamChoices.length" />
                                <SelectItem :value="NEW_TEAM">Create a team…</SelectItem>
                            </SelectContent>
                        </Select>
                        <!-- Chosen by hand, or for want of any team: the new team is created here and chosen above. -->
                        <template v-if="teamId === NEW_TEAM">
                            <div class="flex flex-wrap items-end gap-2">
                                <label class="grid flex-1 gap-1 text-sm">New team name<Input v-model="newTeamName" maxlength="200" :disabled="creatingTeam" @keydown.enter.prevent="createTeam" /></label>
                                <Button type="button" variant="outline" :disabled="busy || creatingTeam || !newTeamName.trim()" @click="createTeam">{{ creatingTeam ? 'Creating…' : 'Create team' }}</Button>
                            </div>
                            <p class="text-muted-foreground text-sm">Created with its name alone; set its lead and goal on its page. Once created, it is chosen here.</p>
                            <p v-if="newTeamError" role="alert" class="text-destructive text-sm">{{ newTeamError }}</p>
                        </template>
                    </div>
                    <div class="grid gap-1 text-sm">
                        <span :id="`${kind}-holder`">Add to teammate</span>
                        <Select :model-value="holder" :disabled="busy" @update:model-value="chooseHolder">
                            <SelectTrigger :aria-labelledby="`${kind}-holder`"><SelectValue placeholder="Choose a teammate" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="member in holderChoices" :key="member.id" :value="member.id">{{ holderLabel(member) }}</SelectItem>
                                <SelectSeparator v-if="holderChoices.length" />
                                <SelectItem :value="NEW_TEAMMATE">Create a new teammate…</SelectItem>
                            </SelectContent>
                        </Select>
                        <p v-if="!creatingTeammate" class="text-muted-foreground text-sm">They join the team if they are not on it yet, and keep their daily spend cap.</p>
                    </div>
                </template>
                <template v-if="creatingTeammate">
                    <div v-for="(_, index) in teammateNames" :key="index" class="space-y-1">
                        <label class="grid gap-1 text-sm">Teammate {{ index + 1 }} name<Input v-model="teammateNames[index]" maxlength="80" :placeholder="teammatePlaceholders[index]" /></label>
                        <p class="text-muted-foreground text-sm">Roles: {{ teammateRoles[index] }}</p>
                    </div>
                    <p class="text-muted-foreground text-sm">Leave a name blank and that teammate goes by its role title until you name it.</p>
                </template>
                <div v-if="creatingTeammate || cooUnnamed" class="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" :disabled="busy || picking || !pickCount" @click="pickNames">Pick names for me</Button>
                    <ZhSystemLabel as="span" tone="muted">From your name and timezone</ZhSystemLabel>
                </div>
                <p v-if="pickError" role="alert" class="text-destructive text-sm">{{ pickError }}</p>
                <ZhPanel v-if="cooUnnamed" class="space-y-3" aria-label="Your COO">
                    <ZhSectionHeading as="h3">Your COO</ZhSectionHeading>
                    <div class="flex items-center gap-3">
                        <ZhGeneratedAvatar ref="cooAvatar" :seed="cooSeed" :label="`Avatar for ${cooName.trim() || cooTitle}`" />
                        <Button type="button" variant="outline" :disabled="busy" @click="avatarRoll++">Generate another</Button>
                    </div>
                    <label class="grid gap-1 text-sm">COO name<Input v-model="cooName" maxlength="80" :placeholder="cooTitle" /></label>
                    <p class="text-muted-foreground text-sm">Saved after cloning. Leave it blank and your COO goes by {{ cooTitle }}.</p>
                </ZhPanel>
                <p class="text-muted-foreground text-sm">Choose the tasks to install. Each keeps its published definition and approval rules.<template v-if="!isTeam && selectedTasks.some(heldByMe)"> A task you already have is left out and your copy stays as it is; one with a newer version can be synced here.</template></p>
                <div v-for="task in selectedTasks" :key="task.id" class="space-y-1 text-sm" :data-testid="`clone-task-${task.id}`">
                    <label class="flex items-center gap-2">
                        <Checkbox :model-value="selectedTaskIds.includes(task.id)" :disabled="busy || (!isTeam && heldByMe(task))" @update:model-value="selectTask(task, $event)" />{{ task.name || task.slug }}
                        <!-- How the task stands with the company, as the Tasks tab's Cloned and Sync say it. -->
                        <ZhStatusPill v-if="heldByMe(task)" :tone="availableVersion(task) !== null ? 'action' : 'completed'" :pulse="false">{{ availableVersion(task) !== null ? 'Update available' : 'Cloned' }}</ZhStatusPill>
                    </label>
                    <p v-if="heldByMe(task)" class="text-muted-foreground flex flex-wrap items-center gap-2">
                        <span v-if="heldLine(task)">{{ heldLine(task) }}</span>
                        <Button v-if="availableVersion(task) !== null" type="button" size="sm" variant="warning" :disabled="busy || syncing[task.id] === 'syncing'" title="The owner has a newer version. Sync makes it your copy's current version; earlier versions stay in its history." @click="syncTask(task)">{{ syncing[task.id] === 'syncing' ? 'Syncing…' : `Sync to ${availableVersion(task)}` }}</Button>
                        <span v-else-if="syncing[task.id] === 'synced'">Synced</span>
                        <span v-if="syncNotes[task.id]" role="alert" class="text-destructive">{{ syncNotes[task.id] }}</span>
                    </p>
                    <p class="text-muted-foreground">{{ task.currentVersion?.goal }}</p>
                    <p>Tools: {{ taskTools(task) }}</p>
                    <!-- #1843: the missing tool itself is announced once, in "Tools this team needs"; the choice stays here. -->
                    <div v-for="tool in missingOf(task.id)" :key="tool" class="grid gap-1">
                        <span :id="`${kind}-swap-${task.id}-${tool}`">What to do about {{ toolName(tool) }}</span>
                        <Select :model-value="choiceOf(task.id, tool)" :disabled="busy" @update:model-value="setChoice(task, tool, $event)">
                            <SelectTrigger :aria-labelledby="`${kind}-swap-${task.id}-${tool}`"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem :value="KEEP">Keep, and connect {{ toolName(tool) }} after cloning</SelectItem>
                                <SelectItem v-for="candidate in candidatesOf(task.id)" :key="candidate" :value="`${USE}${candidate}`">Use {{ toolName(candidate) }} instead</SelectItem>
                                <SelectItem :value="DROP">Drop this task</SelectItem>
                            </SelectContent>
                        </Select>
                        <p v-if="!candidatesOf(task.id).length" class="text-muted-foreground">No connected tool can stand in for {{ toolName(tool) }}.</p>
                    </div>
                    <p>{{ expectedCostPerRun(task.currentVersion?.spend) }}</p>
                    <p>Approvals: {{ approvals(task) || 'No approval gates declared' }}</p>
                </div>
                <p v-if="!selectedTaskIds.length" role="alert" class="text-destructive text-sm">{{ allHeld ? 'Your company already has every task on this role. Give the role to a teammate from their page on Members.' : 'Choose at least one task so your teammates have work to do.' }}</p>
                <!-- #1842: what the company already has of this team, and what the clone adds, from the team review. -->
                <section v-if="cloneContents" class="space-y-2" aria-labelledby="catalog-clone-contents">
                    <ZhSectionHeading id="catalog-clone-contents" as="h3">What you have and what this adds</ZhSectionHeading>
                    <div v-if="cloneContents.existing.length" data-testid="clone-existing" class="space-y-1 text-sm">
                        <p class="font-medium">Already in your company</p>
                        <ul class="text-muted-foreground list-disc space-y-1 pl-5">
                            <li v-for="(line, index) in cloneContents.existing" :key="index">{{ line }}</li>
                        </ul>
                    </div>
                    <div data-testid="clone-added" class="space-y-1 text-sm">
                        <p class="font-medium">Added with this team</p>
                        <ul class="text-muted-foreground list-disc space-y-1 pl-5">
                            <li>{{ cloneContents.teammates }} new {{ cloneContents.teammates === 1 ? 'teammate' : 'teammates' }}</li>
                            <li v-if="cloneContents.roles.length">This team's own roles: {{ cloneContents.roles.join(', ') }}</li>
                            <li v-if="cloneContents.tasks.length">This team's own tasks: {{ cloneContents.tasks.join(', ') }}</li>
                        </ul>
                    </div>
                </section>
                <!-- #1843: the tools the selected tasks need, each with the reader's status and a way to connect it. -->
                <section v-if="toolsSectionShown" class="space-y-2" aria-labelledby="catalog-clone-tools">
                    <ZhSectionHeading id="catalog-clone-tools" as="h3">Tools this team needs</ZhSectionHeading>
                    <p v-if="reviewState === 'loading'" class="text-muted-foreground text-sm">Checking your tools…</p>
                    <template v-else-if="reviewState === 'failed'">
                        <p role="alert" class="text-destructive text-sm">We couldn't check your tools. You can still clone and connect them later.</p>
                        <Button type="button" variant="outline" :disabled="busy" @click="loadToolReview()">Try again</Button>
                    </template>
                    <template v-else>
                        <p v-if="!missingTools.length" class="text-sm">All your tools are connected.</p>
                        <p v-else class="text-muted-foreground text-sm">Connecting is optional here: you can also connect them after cloning.</p>
                        <div v-for="tool in toolRows" :key="tool.toolType" :data-testid="`tool-row-${tool.toolType}`" class="space-y-1 text-sm">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="font-medium">{{ tool.name }}</span>
                                <ZhStatusPill :tone="tool.status === 'connected' ? 'completed' : 'action'" :pulse="false">{{ toolStatusWords(tool.status) }}</ZhStatusPill>
                                <RouterLink v-if="isOwner && tool.status !== 'connected'" :to="connectToolLocation(tool.toolType)" target="_blank" rel="noopener" :aria-label="`Connect ${tool.name}`" @click="startConnect(tool.toolType)">Connect</RouterLink>
                            </div>
                            <p class="text-muted-foreground">Needed by {{ inWords(tool.taskIds.map(taskName)) }}</p>
                            <p v-if="!isOwner && tool.status !== 'connected'" class="text-muted-foreground">Ask an owner of your company to connect {{ tool.name }}.</p>
                            <ZhSystemLabel v-if="tool.status === 'connected' && connectOutcome[tool.toolType] === 'connected'" tone="muted">{{ tool.name }} connected</ZhSystemLabel>
                            <p v-if="tool.status !== 'connected' && connectOutcome[tool.toolType] === 'missing'" role="status" class="text-muted-foreground">{{ tool.name }} isn't connected yet. If sign-in was cancelled or didn't finish, press Connect to try again.</p>
                        </div>
                    </template>
                </section>
                <!-- Tool slots: the tasks name some tools by a slot, which the company fills with a connection of its own. -->
                <section v-if="slotRows.length" class="space-y-2" aria-labelledby="catalog-clone-slots">
                    <ZhSectionHeading id="catalog-clone-slots" as="h3">Slots to fill</ZhSectionHeading>
                    <p class="text-muted-foreground text-sm">These tasks name some of their tools by a slot. Choose which of your connections fills each one; you can change it later on the Tools page.</p>
                    <div v-for="slot in slotRows" :key="slot.key" :data-testid="`slot-row-${slot.key}`" class="grid gap-1 text-sm">
                        <span :id="`${kind}-slot-${slot.key}`" class="font-medium">{{ slot.label }}</span>
                        <p v-if="slot.description" class="text-muted-foreground">{{ slot.description }}</p>
                        <p class="text-muted-foreground">Needed by {{ inWords(slot.taskIds.map(taskName)) }}</p>
                        <Select v-if="slotOptions(slot).length" :model-value="slotChoice(slot)" :disabled="busy" @update:model-value="chooseSlot(slot, $event)">
                            <SelectTrigger :aria-labelledby="`${kind}-slot-${slot.key}`"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem v-if="!slot.filledBy" :value="NO_FILL">Not now, fill it later</SelectItem>
                                <SelectItem v-for="option in slotOptions(slot)" :key="option.bindingId" :value="option.bindingId">{{ option.name }}</SelectItem>
                            </SelectContent>
                        </Select>
                        <p v-else class="text-muted-foreground">No connection can fill this yet. Connect a tool on the <RouterLink to="/tools">Tools page</RouterLink> first.</p>
                        <!-- #957: the actions the tasks name under the slot, and what each connection offered leaves unmapped. -->
                        <template v-if="slot.actions?.length">
                            <p class="text-muted-foreground">The actions these tasks need from it:</p>
                            <ul :data-testid="`slot-actions-${slot.key}`" class="grid gap-1">
                                <li v-for="action in slot.actions" :key="action.name" :data-testid="`slot-action-${action.name}`"><code>{{ action.name }}</code><template v-if="action.description"> · {{ action.description }}</template></li>
                            </ul>
                            <p v-for="option in unmappedOptions(slot)" :key="option.bindingId" :data-testid="`slot-missing-${option.bindingId}`" class="flex flex-wrap items-center gap-2">
                                <ZhStatusPill tone="action" :pulse="false">{{ option.missingActions.length }} unmapped</ZhStatusPill>
                                <span>{{ option.name }} doesn't say which of its tools {{ inWords(option.missingActions) }} {{ option.missingActions.length === 1 ? 'is' : 'are' }} yet.</span>
                                <RouterLink :to="actionsLink(option.bindingId)" target="_blank" rel="noopener">Map {{ option.missingActions.length === 1 ? 'it' : 'them' }} on Tools</RouterLink>
                            </p>
                            <ZhPanel v-if="chosenUnmapped(slot)" as="div" tone="action" role="status" data-testid="slot-actions-warning">
                                {{ chosenUnmapped(slot)?.name }} leaves {{ inWords(chosenUnmapped(slot)?.missingActions ?? []) }} unmapped. You can still clone: a run that needs {{ (chosenUnmapped(slot)?.missingActions.length ?? 0) === 1 ? 'it' : 'one of them' }} waits until the connection's Actions say which tool it is.
                            </ZhPanel>
                        </template>
                    </div>
                </section>
                <!-- #1840: the template's suggested schedules, counted against the plan and confirmed with the clone. -->
                <section v-if="isTeam && suggestedSchedules.length" class="space-y-2" aria-labelledby="catalog-clone-schedules">
                    <ZhSectionHeading id="catalog-clone-schedules" as="h3">Schedules</ZhSectionHeading>
                    <ZhSystemLabel v-if="scheduleCounter" tone="muted">{{ scheduleCounter }}</ZhSystemLabel>
                    <p class="text-muted-foreground text-sm">These tasks come with the schedules they run on. Times are in your company's timezone. A schedule left off arrives paused, and you can switch it on later on the task's Triggers.</p>
                    <div v-for="row in scheduleRows" :key="row.taskId" :data-testid="`schedule-row-${row.taskId}`" class="space-y-1 text-sm">
                        <label class="flex items-center gap-2"><Checkbox :model-value="row.enabled" :disabled="busy || scheduleBlocked(row)" @update:model-value="switchSchedule(row.taskId, $event)" />{{ row.name }}</label>
                        <p class="text-muted-foreground">{{ describeCron(row.cron) }}</p>
                        <label v-if="row.time !== null" class="grid gap-1">Time<Input type="time" :model-value="row.time ?? ''" :aria-label="`Time for ${row.name}`" @update:model-value="setScheduleTime(row.taskId, $event)" /></label>
                        <p v-if="scheduleBlocked(row) && scheduleAllowance" class="text-muted-foreground">Your {{ scheduleAllowance.plan }} plan has no room for another schedule. Switch one off, or <RouterLink to="/billing#plans">upgrade on Billing</RouterLink>.</p>
                    </div>
                </section>
                <!-- #1841: where the cloned team's tasks save what they learn. Nothing to tick: both layers are on by default. -->
                <section v-if="memoryRows.length" class="space-y-2" aria-labelledby="catalog-clone-memory">
                    <ZhSectionHeading id="catalog-clone-memory" as="h3">Memory</ZhSectionHeading>
                    <p class="text-muted-foreground text-sm">This team's tasks save what they learn in your enterprise's memory, as your own tasks do. Each note is marked with the task and run that wrote it. The publisher never sees these notes. Tasks can add new notes but cannot change notes already there. You can withdraw either layer later on the team's page.</p>
                    <div v-for="row in memoryRows" :key="row.layer" :data-testid="`memory-row-${row.layer}`" class="space-y-1 text-sm">
                        <p>{{ memoryAllows(row.layer) }}</p>
                        <ZhSystemLabel tone="muted">{{ row.layer === 'team' ? 'Team memory' : 'Enterprise memory' }}</ZhSystemLabel>
                        <p class="text-muted-foreground">Tasks that could save notes here: {{ row.writers.join(', ') }}</p>
                    </div>
                </section>
                <label v-if="creatingTeammate" class="grid gap-1 text-sm">Daily {{ isTeam ? 'team' : 'teammate' }} spend cap (USD)<Input :model-value="money" type="number" min="0.01" max="10000" step="0.01" required @update:model-value="typeMoney" /></label>
                <ZhPanel class="space-y-2" data-testid="monthly-cost-estimate">
                    <ZhSectionHeading>Expected monthly cost</ZhSectionHeading>
                    <div class="space-y-2" aria-live="polite">
                        <p v-if="connections === undefined" class="text-muted-foreground text-sm">Working out the monthly cost…</p>
                        <p v-for="line in monthlyCost" :key="line" class="text-sm">{{ line }}</p>
                    </div>
                    <p class="text-muted-foreground text-sm">AI account cost only. Your ZeroHuman plan is billed separately.</p>
                </ZhPanel>
                <p v-for="line in missingToolLines" :key="line" class="text-sm">{{ line }}</p>
                <div class="flex gap-2">
                    <Button type="submit" :disabled="!ready">{{ busy ? 'Cloning…' : `Clone ${kind}` }}</Button>
                    <Button type="button" variant="outline" :disabled="busy" @click="selected = null">Cancel</Button>
                </div>
            </fieldset>
        </form>
    </ZhPanel>
</template>
